import { FieldArray } from 'react-final-form-arrays';
import { useCallback, useState } from 'react';

import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpResultViolationSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpResultViolationSelectOption';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';

import { IKnmErpFormViolationsLawsuit } from '../interfaces/IKnmErpFormViolationsLawsuit';
import styles from './KnmErpLawsuit.module.scss';

const COLLAPSE_TITLE_BASE = 'Мера';

interface IKnmErpLawsuitProps {
  formRef: IKnmFormRef | undefined;
  id: string;
  index: number;
  knmResultsViolationsSelectOptions: IKnmErpResultViolationSelectOption[] | undefined;
  name: string;
  value: IKnmErpFormViolationsLawsuit | undefined;
  violationsLawsuits: IKnmForm['violationsLawsuits'];
  watchOnly?: boolean;
  onRemove: () => void;
}

export const KnmErpLawsuit = ({
  formRef,
  id,
  index,
  knmResultsViolationsSelectOptions,
  name,
  value,
  violationsLawsuits,
  watchOnly,
  onRemove,
}: IKnmErpLawsuitProps) => {
  const [isOpen, setIsOpen] = useState(value?.isNew);
  const [isTrigger, setIsTrigger] = useState(false);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const addLawsuit = useCallback(() => !watchOnly && push && push(`${name}.lawsuits`, {}), [name, push, watchOnly]);
  const makeRemoveLawsuitHandler = (i: number) => () => !watchOnly && remove && remove(`${name}.lawsuits`, i);

  const customKnmResultsViolationsSelectOptions:
    | IKnmErpResultViolationSelectOption[]
    | undefined = knmResultsViolationsSelectOptions?.map(knmResultViolationSelectOption => ({
    ...knmResultViolationSelectOption,
    isDisabled: violationsLawsuits?.some(
      violationLawsuit =>
        knmResultViolationSelectOption.id === violationLawsuit?.violation?.id &&
        knmResultViolationSelectOption.id !== value?.violation?.id,
    ),
  }));

  const onToggle = useCallback(() => {
    setIsOpen(!isOpen);
    setTimeout(() => {
      setIsTrigger(!isTrigger);
    });
  }, [isOpen, isTrigger]);

  return (
    <Row>
      <KnmCollapse
        id={id}
        theme="fields"
        title={value?.isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
        subtitle={value?.violation?.label}
        isNew={value?.isNew}
        openByDefault={value?.isNew}
        onClose={onRemove}
        onToggle={onToggle}
        opened={isOpen}
      >
        <>
          <Row>
            <FieldBlock label="Нарушение">
              <SelectInputField
                name={`${name}.violation`}
                options={customKnmResultsViolationsSelectOptions}
                disabled={watchOnly}
                size="medium"
              />
            </FieldBlock>
          </Row>

          <FieldBlock
            label="Сведения о мерах, принятых по результатам КНМ"
            button={
              <Button
                className="shared-btn-add"
                text="Добавить"
                variant="link"
                color="primary"
                type="button"
                disabled={watchOnly}
                onClick={addLawsuit}
              />
            }
          >
            <FieldArray name={`${name}.lawsuits`}>
              {({ fields }) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((lawsuitsFieldName, lawsuitIndex) => (
                  <Row isListItem margin="medium" key={lawsuitsFieldName} notWrap>
                    <div className="shared-deleting-row">
                      <SelectInputFieldAutoOptions
                        name={`${lawsuitsFieldName}.lawsuitType`}
                        placeholder="Выберите сведения о принятых мерах"
                        optionName={CommonCatalogsNamesEnum.VIOLATION_LAWSUIT_TYPES}
                        disabled={watchOnly}
                        size="medium"
                      />
                      <TextareaField
                        className={styles.LawsuitTextField}
                        name={`${lawsuitsFieldName}.text`}
                        placeholder="Введите сведения о принятых мерах"
                        disabled={watchOnly}
                        validate={makeRequired('Поле не может быть пустым')}
                        triggerCalculateHeight={isTrigger}
                      />
                    </div>
                    {fields.length && fields.length > 1 && (
                      <CloseButton
                        className="shared-deleting-row-btn"
                        onClick={makeRemoveLawsuitHandler(lawsuitIndex)}
                      />
                    )}
                  </Row>
                ));
              }}
            </FieldArray>
          </FieldBlock>
        </>
      </KnmCollapse>
    </Row>
  );
};
